import { useState } from 'react'
import { useNotification } from 'hooks'
import { Table } from 'components/core-ui/Table'
import { AddActionIcon, DeleteActionIcon, EditActionIcon } from 'icons'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { formatErrorMessage } from 'utils/formatting'
import { columns, drawerFilters } from './helpers'
import { toTableRowItems } from './serializers'
import { DLCProductsForm } from './DLCProductsForm'
import { ImportProductsForm } from './ImportProductsForm'
import s from '../../../commonStyles.modules.scss'
import { DeleteConfirmationModal } from 'components/core-ui/DeleteConfirmationModal'
import { Typography } from '@mui/material'
import {
  getMetiFamiliesOptions,
  getMetiSubFamiliesOptions,
  getMetiUBOptions,
  getRayonsOptions,
  getScreenPermissions,
  getShopOptions,
  getShopTypesOptions
} from '../../api'
import { SCREENS } from '../../constants'

const DLCProducts = () => {
  const { showNotification } = useNotification()

  const [tableItems, setTableItems] = useState<DLCProducts[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<DLCProducts>()

  const [createUpdateModal, setCreateUpdateModal] = useState<string>(null)
  const openCreateModal = (): void => setCreateUpdateModal('create')
  const openUpdateModal = (): void => setCreateUpdateModal('update')
  const closeCreateUpdateModal = (): void => setCreateUpdateModal(null)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const openDeleteModal = (): void => setIsDeleteModalOpen(true)
  const closeDeleteModal = (): void => setIsDeleteModalOpen(false)

  const [isImportProductsModalOpen, setIsImportProductsModalOpen] = useState<boolean>(false)
  const openImportProductsModal = (): void => setIsImportProductsModalOpen(true)
  const closeImportProductsModal = (): void => setIsImportProductsModalOpen(false)

  const selectRow = (rowId: number | string) => {
    const row = tableItems.find(item => item.id === rowId)
    if (row) setSelectedRow(row)
  }

  const { data: permissions } = getScreenPermissions(showNotification, SCREENS.ADMIN_DLC)

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/shop-reception/products/short-dlc',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list))
      setTotalCount(response?.page?.totalCount || 0)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor din tabel. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const { data: shopTypes, isLoading: isLoadingShopTypes } = getShopTypesOptions(showNotification)

  const { data: shopCodes, isLoading: isShopCodesLoading } = getShopOptions(showNotification)

  const { data: metiRayons } = getRayonsOptions(showNotification)

  const { data: metiFamilies } = getMetiFamiliesOptions(showNotification)

  const { data: metiSubfamilies } = getMetiSubFamiliesOptions(showNotification)

  const { data: metiUB } = getMetiUBOptions(showNotification)

  const combineShopOptions = (shopTypes, shopCodes) => {
    const mappedShopTypes = (shopTypes || []).map(({ label, value }) => ({
      label: label,
      value: value
    }));

    return [
      ...mappedShopTypes,
      ...(shopCodes || [])
    ];
  };

  const combinedShopOptions = combineShopOptions(shopTypes, shopCodes);

  return (
    <div className={s.mainContainer}>
      <ImportProductsForm
        isModalOpen={isImportProductsModalOpen}
        closeModal={closeImportProductsModal}
        setShouldRefreshData={setShouldRefreshData}
        shopTypes={shopTypes}
        shopCodes={shopCodes}
        isLoadingShopTypes={isLoadingShopTypes}
        isShopCodesLoading={isShopCodesLoading}
      />
      <DLCProductsForm
        isOpen={createUpdateModal === 'create'}
        closeModal={closeCreateUpdateModal}
        setShouldRefreshData={setShouldRefreshData}
        metiRayons={metiRayons}
        shopCodes={combinedShopOptions}
      />
      <DLCProductsForm
        isOpen={createUpdateModal === 'update'}
        closeModal={closeCreateUpdateModal}
        setShouldRefreshData={setShouldRefreshData}
        metiRayons={metiRayons}
        editItem={selectedRow}
        shopCodes={combinedShopOptions}
        isEdit
      />
      <DeleteConfirmationModal
        isModalOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        actionUrl="/shop-reception/products/short-dlc/:id"
        itemId={`${selectedRow?.id}`}
        content={<Typography>Esti sigur va vrei sa stergi acest produs?</Typography>}
        setShouldRefreshData={setShouldRefreshData}
        deleteLabel="Sterge"
        cancelLabel="Anuleaza"
        succesMessage="Produsul a fost sters cu succes."
      />
      <Table
        hideCheckbox
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        filters={drawerFilters(combinedShopOptions, metiRayons, metiFamilies, metiSubfamilies, metiUB)}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        rowActions={[
          {
            ...(permissions?.canEdit && {
              label: 'Edit',
              labelColor: 'white',
              typographyVariant: 'medium',
              backgroundColor: '#16a085',
              desktopIcon: <EditActionIcon />,
              onClick: (e, rowId) => {
                selectRow(rowId)
                openUpdateModal()
              }
            })
          },
          {
            ...(permissions?.canDelete && {
              label: 'Delete',
              labelColor: 'white',
              typographyVariant: 'medium',
              backgroundColor: '#16a085',
              desktopIcon: <DeleteActionIcon />,
              onClick: (e, rowId) => {
                selectRow(rowId)
                openDeleteModal()
              }
            })
          }
        ]}
        rowActionsWidth="130px"
        toolbarActions={
          permissions?.canAdd
            ? [
                {
                  label: 'Importǎ produse DLC scurt',
                  icon: <AddActionIcon />,
                  onClick: openImportProductsModal
                },
                {
                  label: 'Produse DLC scurt',
                  icon: <AddActionIcon />,
                  onClick: openCreateModal
                }
              ]
            : []
        }
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default DLCProducts
